import React, { useState, useLayoutEffect, useRef, memo } from "react"
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from "react-virtualized"
import { Button } from "../Button"
import { InfoTooltip } from "../Tooltip"
import { IoQrCode } from "react-icons/io5"
import QRCodeComponent from "../QrCode"
import { getMessageLink } from "../../container/QRCodeConfigContainer"
import { v4 as uuid } from "uuid"
import { useCopyClipBoard } from "../../core/utils/hooks/copyToClipBoard"
import { Spinner } from "../Loading"
import "./styles.scss"
import ModalAlert from "../ModalAlert"

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 500,
})

const AccordionHeader = memo(({ title, link, id, handleDeleteQrCode, copyToClipBoard }) => (
  <div className="accordion__header-container">
    <div className="accordion__header-title">
      <IoQrCode className="icon_content" color="#0146ad" size={24} />
      <span>{title}</span>
    </div>
    <div className="accordion__header-actions">
      <InfoTooltip info="Excluir" trigger="hover">
        <button
          onClick={() =>
            ModalAlert.warn({
              title: "Deseja continuar ?",
              text: "Deseja excluir este QrCode ?",
              confirmLabel: "Excluir",
              onConfirm: () => handleDeleteQrCode(id),
            })
          }>
          <i className="fi fi-rr-trash"></i>
        </button>
      </InfoTooltip>
      <InfoTooltip info="Copiar Link">
        <button onClick={() => copyToClipBoard(link, "Link copiado com sucesso")}>
          <i className="fi fi-rr-copy"></i>
        </button>
      </InfoTooltip>
    </div>
  </div>
))

export const QrCodeHistoryList = ({ data, loading, handleDeleteQrCode }) => {
  const copyToClipBoard = useCopyClipBoard()
  const qrCodeRef = useRef(null)

  const downloadCode = (id, title, project) => {
    const canvas = document.getElementById(`QRCode${id}`)
    if (canvas) {
      const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream")
      const downloadLink = document.createElement("a")
      downloadLink.href = pngUrl
      downloadLink.download = `${title}_${project}_${uuid()}.png`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    }
  }

  const Collapsible = ({ children, data, onChange }) => {
    const [expanded, setExpanded] = useState(false)
    const [rotate, setRotateIcon] = useState("accordion__icon")

    const { qrCodeTitle, project, id } = data

    const toggleExpanded = () => {
      setExpanded((prevExpanded) => !prevExpanded)
      setRotateIcon((prev) =>
        prev === "accordion__icon" ? "accordion__icon rotate" : "accordion__icon",
      )
    }

    useLayoutEffect(() => {
      onChange()
    }, [expanded, onChange])

    
    return (
      <>
        <div className={`accordion-header ${expanded ? "active" : "inactive"}`}>
          <AccordionHeader handleDeleteQrCode={handleDeleteQrCode} title={qrCodeTitle} link={`https://conexao.impactools.social/qr-code/${id}`} project={project?.name} id={id} copyToClipBoard={copyToClipBoard} />
          <i
            className={`fi fi-rr-angle-small-right icon-down ${rotate}`}
            onClick={toggleExpanded}
          />
        </div>
        {expanded && (
          <div className={`inner-content ${expanded ? "active" : "inactive"}`}>{children}</div>
        )}
      </>
    )
  }

  const Row = ({ index, key, style, parent }) => {
    const item = data[index]

    const content = (
      <div className="qrcode__accordion-content">
        <div className="qrcode__accordion-content__image-actions">
          <div className="actions-qrarea">
            <QRCodeComponent
              content={`https://conexao.impactools.social/qr-code/${item.id}`}
              id={item?.id}
              size={180}
              ref={qrCodeRef}
            />
          </div>
          <div className="actions-buttons-area">
            <Button
              label="Baixar QRCode"
              handleClick={() => downloadCode(item?.id, item?.qrCodeTitle, item?.project?.name)}
            />
            <Button
              label="Copiar link"
              variant="outline"
              handleClick={() => copyToClipBoard(
                `https://conexao.impactools.social/qr-code/${item.id}`,
                "Link copiado com sucesso"
              )}
            />
          </div>
        </div>
        <div className="qrcode__accordion-content__details">
          <div className="details-messages">
            <span className="details-messages__label">
              Mensagem que será recebida do beneficiário
            </span>
            <p className="details-messages__text">{item.receivedMessage}</p>
          </div>
          {(item.feedbackMessage && item.feedbackMessage != "-") && <div className="details-messages">
            <span className="details-messages__label">
              Mensagem que será enviada para o beneficiário
            </span>
            <p className="details-messages__text">{item.feedbackMessage}</p>
          </div>}
        </div>
      </div>
    )

    return (
      <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
        {({ registerChild, measure }) => (
          <div style={style} className="collapse-row" ref={registerChild}>
            <Collapsible data={item} onChange={measure}>
              {content}
            </Collapsible>
          </div>
        )}
      </CellMeasurer>
    )
  }

  return (
    <div className="qrcode__accordion">
      <AutoSizer>
        {({ width, height }) => (
          <>
            {loading ? (
              <div className="loading-content" style={{ height, width }}>
                <Spinner color="primary" />
              </div>
            ) : data.length > 0 ? (
              <>
                <List
                  width={width}
                  height={height}
                  deferredMeasurementCache={cache}
                  rowHeight={cache.rowHeight}
                  rowRenderer={Row}
                  rowCount={data.length}
                  overscanRowCount={0}
                  className="qrcode__accordion-list"
                />
              </>
            ) : (
              <div className="channels-empty" style={{ height, width }}>
                <h3>Não há Qr Code para este projeto</h3>
              </div>
            )}
          </>
        )}
      </AutoSizer>
    </div>
  )
}
